import { merge } from 'rxjs'
import { debounceTime, filter as rxFilter, ignoreElements, map, tap, withLatestFrom } from 'rxjs/operators'
import type { UnknownAction } from '@reduxjs/toolkit'
import { Actions } from '../../Slice/index.js'
import { visualizationSlice } from '../../Slice/Visualization/VisualizationSlice.js'
import { isAnyOf } from '@reduxjs/toolkit'
import { saveAutosave } from '../../../View/Component/ConfigurableVisualization/Persistence/StorageNext.js'
import type { Epic } from '../../index.js'
import { pickAutosave } from '../../../View/Component/ConfigurableVisualization/Persistence/Pickers.js'

/**
 * The grid reports its layout on mount and sinks zoom on their own, so these fire without the user doing
 * anything. While autosave is held they must not release it, or opening the workspace would overwrite the
 * session the user is being offered to resume.
 */
const isAppInitiated = (action: UnknownAction): boolean =>
  visualizationSlice.actions.updateGridLayouts.match(action) ||
  (visualizationSlice.actions.setTaxaZoom.match(action) && action.payload.auto === true)

export const autosaveVizEpic: Epic = (action$, state$) => {
  const visualizationActions = Actions[visualizationSlice.name]

  const edits$ = action$.pipe(
    rxFilter(
      isAnyOf(
        visualizationActions.createSource,
        visualizationActions.removeSource,
        visualizationActions.setSourceConfig,
        visualizationActions.setSourceState,
        visualizationActions.updateSourceShared,
        visualizationActions.updateSourceName,
        visualizationActions.registerSink,
        visualizationActions.unregisterSink,
        visualizationActions.updateGridLayouts,
        visualizationActions.addPanelsToLayouts,
        visualizationActions.removePanelsBySink,
        visualizationActions.renamePanel,
        visualizationActions.goBackOneTaxaLevel,
        visualizationActions.setTaxaZoom,
        visualizationActions.resetTaxaZoom,
        visualizationActions.updateNatureKpis
      )
    )
  )

  const release$ = edits$.pipe(
    withLatestFrom(state$),
    rxFilter(([action, s]) => s.visualization.autosaveHeld && !s.visualization.__restoring && !isAppInitiated(action)),
    map(() => visualizationActions.releaseAutosaveHold())
  )

  const save$ = edits$.pipe(
    debounceTime(500),
    withLatestFrom(state$),
    rxFilter(([, s]) => !s.visualization.__restoring && !s.visualization.autosaveHeld),
    tap(([, s]) => {
      const scopeId = s.visualization.scope?.id
      if (!scopeId) return
      const payload = pickAutosave(s.visualization)
      saveAutosave(scopeId, payload)
    }),
    ignoreElements()
  )

  return merge(release$, save$)
}
